import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Clock, FileText, Trophy, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TestHistory() {
  const [history, setHistory] = useState([]);
  const [filterType, setFilterType] = useState("all");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const params = filterType !== "all" ? `?filter_type=${filterType}` : "";
    axios.get(`${API}/test-history${params}`).then(r => { setHistory(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [filterType]);

  const formatDuration = (seconds) => {
    if (!seconds) return "N/A";
    const m = Math.floor(seconds / 60);
    return `${m} min`;
  };

  return (
    <div className="p-8 lg:p-12 space-y-8 animate-fade-in" data-testid="test-history-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight">Test History</h1>
          <p className="text-muted-foreground mt-2 text-base">{history.length} tests completed</p>
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger data-testid="history-filter" className="w-[180px]">
            <SelectValue placeholder="Filter" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Questions</SelectItem>
            <SelectItem value="wrong">Wrong Only</SelectItem>
            <SelectItem value="unattempted">Unattempted Only</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-24 bg-secondary rounded animate-pulse" />)}</div>
      ) : history.length === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="p-12 text-center">
            <FileText size={48} className="mx-auto mb-4 text-muted-foreground opacity-50" />
            <p className="text-muted-foreground mb-4">No tests taken yet</p>
            <Button data-testid="start-first-test-btn" onClick={() => navigate("/test/setup")} className="bg-primary hover:bg-primary/90">
              Take First Test
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {history.map((h, i) => {
            const score = h.score || {};
            return (
              <Card
                key={h.attempt_id}
                data-testid={`history-item-${i}`}
                className="bg-card border-border hover:border-primary/30 transition-all cursor-pointer"
                onClick={() => navigate(`/test/${h.test_id}/results`)}
              >
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-sm bg-primary/10 flex items-center justify-center">
                        {h.is_redemption ? <Trophy size={20} className="text-amber-400" /> : <FileText size={20} className="text-primary" />}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{h.test_code}</p>
                          {h.is_redemption && <Badge className="bg-amber-500/20 text-amber-400 text-xs border-0">Redemption</Badge>}
                          <Badge variant="outline" className="text-xs capitalize">{h.mode}</Badge>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                          <span className="flex items-center gap-1"><Clock size={12} /> {h.submitted_at ? new Date(h.submitted_at).toLocaleDateString() : "N/A"}</span>
                          <span>{h.question_count} questions</span>
                          <span>{formatDuration(h.time_taken_seconds)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-mono text-2xl font-bold">{score.percentage?.toFixed(1) || 0}%</p>
                        <p className="text-xs text-muted-foreground">
                          {score.correct || 0} correct, {score.incorrect || 0} wrong
                        </p>
                      </div>
                      {filterType !== "all" && h.filtered_count !== undefined && (
                        <Badge variant="outline" className="text-xs">
                          {h.filtered_count} {filterType}
                        </Badge>
                      )}
                      <ArrowRight size={16} className="text-muted-foreground" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
